'use client';

import { c as _c } from "react/compiler-runtime";
import { useCreateOutcomeSuggestion, useUploadMarketImages } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import type { MarketImageDraft } from '@/components/images/market-picker';
import { OptimizedImage } from '@/components/images/optimized-image';
import { OutcomeInput } from '@/components/launchpad/outcome-input';
import { StepIndicator } from '@/components/launchpad/step-indicator';
import { TradingRestrictedNotice } from '@/components/trade/trading-restricted-notice';
import { AnimatedButton } from '@/components/ui/animated-button';
import { CheckboxField } from '@/components/ui/checkbox-field';
import { Icon } from '@/components/ui/icon';
import { Alert01Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
import { MotionAnimatedAutoHeight, type MotionAnimatedAutoHeightDependency } from '@/components/ui/motion-animated-auto-height';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TextareaField } from '@/components/ui/textarea-field';
import { useOutcomesLimits } from '@/hooks/launchpad/use-outcomes-limits';
import { useTradingRestricted } from '@/hooks/trading/use-trading-restricted';
import { reportError } from '@/lib/telemetry';
import { DURATION_EXIT_MS } from '@/lib/ui/easing';
import { manualSuggestOutcomeStore, SuggestOutcomeWizardStoreProvider, useSuggestOutcomeWizardStore, type WizardStep } from '@/stores/suggest-outcome-wizard';
import { DuplicateErrorBanner } from '../duplicate-error/DuplicateErrorBanner';
import type { SuggestionWire } from '../types';
interface SuggestOutcomeWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  marketPubkey: string;
  /**
   * The proposal/market title — seeds the MarketImagePickerModal's AI
   * image search so query results pick up the market's context (e.g.
   * "Andy Murray" + market "Who wins Wimbledon 2026?" → better seed
   * than "Andy Murray" alone). Optional; empty falls back to label-only
   * search.
   */
  marketTitle?: string;
  existingOutcomeLabels: ReadonlyArray<string>;
  marketOutcomeCount: number | null;
  existingSuggestionLabels: ReadonlyArray<string>;
  remainingSuggestions: number;
  creatorDirectAdd?: boolean;
  onSubmitted: (suggestion: SuggestionWire) => void;
}
const TOTAL_STEPS: number = 2;
interface ConflictState {
  kind: 'suggestion' | 'onchain_outcome';
  label: string;
  upvotes: number;
  downvotes: number;
  id: string;
}

/**
 * Outer wrapper keeps every `useSuggestOutcomeWizardStore` call inside the
 * provider. The flow is manual-only; AI single-outcome generation was removed.
 */
export function SuggestOutcomeWizard(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <SuggestOutcomeWizardStoreProvider><SuggestOutcomeWizardInner {...props} /></SuggestOutcomeWizardStoreProvider>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
type SuggestOutcomeWizardInnerProps = SuggestOutcomeWizardProps;
function SuggestOutcomeWizardInner({
  open,
  onOpenChange,
  marketPubkey,
  marketTitle = '',
  marketOutcomeCount,
  remainingSuggestions,
  creatorDirectAdd = false,
  onSubmitted
}: SuggestOutcomeWizardInnerProps): React.ReactNode {
  const t = useTranslations('launchpad.suggestedOutcomes.wizard');
  const store = useSuggestOutcomeWizardStore();
  const mutation = useCreateOutcomeSuggestion();
  // Only the creator's direct add puts an outcome on chain; plain suggestions are posts.
  const addBlocked = useTradingRestricted() && creatorDirectAdd;
  const [conflict, setConflict] = useState<ConflictState | null>(null);

  // Clear-draft confirmation modal state.
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [disclaimerAcked, setDisclaimerAcked] = useState(false);
  const totalSteps = TOTAL_STEPS;

  // Reset navigation state on every open, but preserve field drafts via
  // Zustand persist. Mirrors create-proposal's open-handler.
  useEffect(() => {
    if (!open) return;
    setDisclaimerAcked(false);
    manualSuggestOutcomeStore.setState({
      step: 1
    });
  }, [open]);
  function handleAdvanceStep(): void {
    const next = Math.min(totalSteps, store.step + 1) as WizardStep;
    store.setStep(next);
  }
  function handleClose(next_0: boolean): void {
    if (next_0) return;
    setConflict(null);
    onOpenChange(false);
  }
  async function handleSubmit(): Promise<void> {
    setConflict(null);
    // Defense in depth: the button is gated on this same predicate, but never
    // send a local preview or missing image if submission is invoked another way.
    if (!isResolvedImageUrl(store.imageUrl)) return;
    try {
      const created = await mutation.mutateAsync({
        pubkey: marketPubkey,
        label: store.label,
        ticker: store.ticker,
        // ALLOW-NSFW-STRIP: image already screened by the image-search picker upstream; backend re-validates via lookupNsfwVerdicts at create() time.
        imageUrl: store.imageUrl,
        imageQuery: store.imageQuery,
        aiPrompt: null,
        generationMode: 'manual',
        reason: store.reason.length > 0 ? store.reason : null
      });
      onSubmitted(created as SuggestionWire);
      onOpenChange(false);
      // Success → reset the draft so a future Suggest starts from a blank
      // slate. Defer the
      // reset until AFTER the sheet's exit animation finishes: `reset()`
      // snaps `step` back to 1, and the body is still mounted (animating
      // out) at this point, so resetting synchronously renders a brief
      // flash of the first wizard step before the sheet disappears.
      const storeToReset = store;
      window.setTimeout((): void => storeToReset.reset(), DURATION_EXIT_MS + 50);
    } catch (err: unknown) {
      // SILENT-CATCH: the conflict branch is the success path (banner UX); other backend errors surface via the global MutationErrorListener.
      const parsed = parseConflictError(err);
      if (parsed !== null) {
        setConflict(parsed);
        return;
      }
      // Any other backend failure is surfaced by the global
      // `MutationErrorListener` (src/providers/mutation-error-listener.tsx)
      // which reads `SdkError.apiCode` and resolves an informative i18n
      // key with the error's details interpolated as placeholders. Firing a local toast here
      // would duplicate that and overwrite the specific message with a
      // generic "Couldn't submit outcome" string. The wizard stays open
      // on the final step so the user can retry or edit before
      // resubmitting.
    }
  }
  const stepTitle = deriveStepTitle({
    step: store.step,
    creatorDirectAdd,
    t
  });
  const remainingText = creatorDirectAdd ? '' : t('remaining', {
    remaining: remainingSuggestions
  });
  const {
    maxOutcomes
  } = useOutcomesLimits(marketPubkey);
  const marketAtCapacity = maxOutcomes !== null && marketOutcomeCount !== null && marketOutcomeCount >= maxOutcomes;
  const capacityText = maxOutcomes !== null && marketOutcomeCount !== null ? t('marketCapacity', {
    current: marketOutcomeCount,
    max: maxOutcomes
  }) : '';
  const sheetHeader = <WizardSheetHeader stepTitle={stepTitle} remainingText={remainingText} capacityText={capacityText} reviewHelpText={creatorDirectAdd ? '' : t('reviewHelp')} marketAtCapacity={marketAtCapacity} currentStep={store.step} totalSteps={totalSteps} />;

  // Image guard applies once the user has left step 1.
  const isImageResolved = isResolvedImageUrl(store.imageUrl);
  const blockedByMissingImage = store.step > 1 && !isImageResolved;
  const reasonRequired = !creatorDirectAdd;
  const canAdvance = canAdvanceStep(store, totalSteps, reasonRequired);
  const isFinalStep = store.step === totalSteps;
  const submitting = mutation.isPending;
  const submitDisabled = !canAdvance || blockedByMissingImage || submitting || marketAtCapacity || reasonRequired && store.reason.trim().length === 0 || creatorDirectAdd && !disclaimerAcked || addBlocked;

  // Draft detection — drives the footer pill.
  const hasDraft = store.label.trim().length > 0 || store.ticker.trim().length > 0 || store.imageUrl.length > 0 || store.reason.trim().length > 0;
  const sheetFooter = <WizardSheetFooter step={store.step} submitting={submitting} hasDraft={hasDraft} isFinalStep={isFinalStep} creatorDirectAdd={creatorDirectAdd} submitDisabled={submitDisabled} canAdvance={canAdvance} blockedByMissingImage={blockedByMissingImage} onBack={(): void => {
    const prevStep = Math.max(1, store.step - 1) as WizardStep;
    store.setStep(prevStep);
  }} onClearDraft={(): void => setConfirmingClear(true)} onSubmit={handleSubmit} onAdvance={handleAdvanceStep} />;
  let body: React.ReactNode | null = null;
  if (store.step === 1) {
    body = <StepManualOutcome marketTitle={marketTitle} />;
  } else if (creatorDirectAdd) {
    body = <StepDisclaimer ackChecked={disclaimerAcked} onAckChange={setDisclaimerAcked} />;
  } else {
    body = <StepReasonConfirm creatorDirectAdd={false} />;
  }
  return <>
      <WizardBodySheet open={open} onOpenChange={handleClose} title={stepTitle} header={sheetHeader} footer={sheetFooter} layoutDependencies={[store.step]} conflict={conflict} onDismissConflict={(): void => setConflict(null)}>
        <TradingRestrictedNotice applies={creatorDirectAdd} />
        {body}
      </WizardBodySheet>

      <ClearDraftConfirmSheet open={confirmingClear} onOpenChange={setConfirmingClear} onConfirm={(): void => {
      store.reset();
      setConfirmingClear(false);
    }} />
    </>;
}
interface WizardBodySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  header: React.ReactNode;
  footer: React.ReactNode;
  layoutDependencies: ReadonlyArray<MotionAnimatedAutoHeightDependency>;
  conflict: ConflictState | null;
  onDismissConflict: () => void;
  children: React.ReactNode | null;
}
function WizardBodySheet(t0) {
  const $ = _c(14);
  const {
    open,
    onOpenChange,
    title,
    header,
    footer,
    layoutDependencies,
    conflict,
    onDismissConflict,
    children
  } = t0;
  let t1;
  if ($[0] !== conflict || $[1] !== onDismissConflict) {
    t1 = conflict !== null ? <DuplicateErrorBanner conflict={conflict} onDismiss={onDismissConflict} /> : null;
    $[0] = conflict;
    $[1] = onDismissConflict;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== layoutDependencies || $[5] !== t1) {
    t2 = <MotionAnimatedAutoHeight layoutDependencies={layoutDependencies} innerClassName="flex flex-col gap-4">{children}{t1}</MotionAnimatedAutoHeight>;
    $[3] = children;
    $[4] = layoutDependencies;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== footer || $[8] !== header || $[9] !== onOpenChange || $[10] !== open || $[11] !== t2 || $[12] !== title) {
    t3 = <SilkSheet open={open} onOpenChange={onOpenChange} variant="page" size="lg" title={title} srOnlyHeader={true} header={header} footer={footer} disableDesktopSwipe={true}>{t2}</SilkSheet>;
    $[7] = footer;
    $[8] = header;
    $[9] = onOpenChange;
    $[10] = open;
    $[11] = t2;
    $[12] = title;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
interface WizardSheetHeaderProps {
  stepTitle: string;
  remainingText: string;
  capacityText: string;
  reviewHelpText: string;
  marketAtCapacity: boolean;
  currentStep: WizardStep;
  totalSteps: number;
}
function WizardSheetHeader(t0) {
  const $ = _c(20);
  const {
    stepTitle,
    remainingText,
    capacityText,
    reviewHelpText,
    marketAtCapacity,
    currentStep,
    totalSteps
  } = t0;
  let t1;
  if ($[0] !== stepTitle) {
    t1 = <span className="text-heading-md">{stepTitle}</span>;
    $[0] = stepTitle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== remainingText) {
    t2 = remainingText.length > 0 ? <span className="text-caption text-foreground-tertiary">{remainingText}</span> : null;
    $[2] = remainingText;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-baseline gap-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== capacityText || $[8] !== marketAtCapacity) {
    t4 = capacityText.length > 0 ? <p className={marketAtCapacity ? "text-caption text-feedback-warning-content" : "text-caption text-foreground-tertiary"} aria-live="polite">{capacityText}</p> : null;
    $[7] = capacityText;
    $[8] = marketAtCapacity;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== reviewHelpText) {
    t5 = reviewHelpText.length > 0 ? <p className="text-caption text-foreground-tertiary">{reviewHelpText}</p> : null;
    $[10] = reviewHelpText;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== currentStep || $[13] !== totalSteps) {
    t6 = <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />;
    $[12] = currentStep;
    $[13] = totalSteps;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6) {
    t7 = <div className="flex flex-col gap-2">{t3}{t4}{t5}{t6}</div>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
interface WizardSheetFooterProps {
  step: WizardStep;
  submitting: boolean;
  hasDraft: boolean;
  isFinalStep: boolean;
  creatorDirectAdd: boolean;
  submitDisabled: boolean;
  canAdvance: boolean;
  blockedByMissingImage: boolean;
  onBack: () => void;
  onClearDraft: () => void;
  onSubmit: () => void;
  onAdvance: () => void;
}
function WizardSheetFooter(t0) {
  const $ = _c(23);
  const {
    step,
    submitting,
    hasDraft,
    isFinalStep,
    creatorDirectAdd,
    submitDisabled,
    canAdvance,
    blockedByMissingImage,
    onBack,
    onClearDraft,
    onSubmit,
    onAdvance
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes.wizard");
  let t1;
  if ($[0] !== onBack || $[1] !== step || $[2] !== submitting || $[3] !== t) {
    t1 = step > 1 ? <AnimatedButton type="button" variant="secondary" onClick={onBack} disabled={submitting}>{t("back")}</AnimatedButton> : <span aria-hidden="true" />;
    $[0] = onBack;
    $[1] = step;
    $[2] = submitting;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== hasDraft || $[6] !== onClearDraft) {
    t2 = hasDraft && <DraftSavedPill onClearDraft={onClearDraft} />;
    $[5] = hasDraft;
    $[6] = onClearDraft;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== blockedByMissingImage || $[9] !== canAdvance || $[10] !== creatorDirectAdd || $[11] !== isFinalStep || $[12] !== onAdvance || $[13] !== onSubmit || $[14] !== step || $[15] !== submitDisabled || $[16] !== submitting || $[17] !== t) {
    t3 = isFinalStep ? <AnimatedButton type="button" variant="primary" onClick={onSubmit} disabled={submitDisabled} loading={submitting}>{submitting ? t("submitting") : creatorDirectAdd ? t("addOutcome") : t("submit")}</AnimatedButton> : <AnimatedButton type="button" variant="primary" onClick={onAdvance} disabled={!canAdvance || step === 2 && blockedByMissingImage}>{t("next")}</AnimatedButton>;
    $[8] = blockedByMissingImage;
    $[9] = canAdvance;
    $[10] = creatorDirectAdd;
    $[11] = isFinalStep;
    $[12] = onAdvance;
    $[13] = onSubmit;
    $[14] = step;
    $[15] = submitDisabled;
    $[16] = submitting;
    $[17] = t;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== t1 || $[20] !== t2 || $[21] !== t3) {
    t4 = <div className="flex items-center justify-between gap-3">{t1}{t2}{t3}</div>;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  return t4;
}
interface ClearDraftConfirmSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/** Clear-draft confirmation. Mirrors create-proposal — confirms before
 *  wiping the persisted localStorage entry. */
function ClearDraftConfirmSheet(t0) {
  const $ = _c(25);
  const {
    open,
    onOpenChange,
    onConfirm
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes.wizard");
  let t1;
  if ($[0] !== t) {
    t1 = t("clearDraftConfirmTitle");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("clearDraftConfirmBody");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onOpenChange) {
    t3 = () => onOpenChange(false);
    $[4] = onOpenChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("cancel");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <AnimatedButton type="button" variant="secondary" onClick={t3}>{t4}</AnimatedButton>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("clearDraftConfirmAction");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== onConfirm || $[14] !== t6) {
    t7 = <AnimatedButton type="button" variant="destructive" onClick={onConfirm}>{t6}</AnimatedButton>;
    $[13] = onConfirm;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <div className="flex justify-end gap-2">{t5}{t7}</div>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== onOpenChange || $[20] !== open || $[21] !== t1 || $[22] !== t2 || $[23] !== t8) {
    t9 = <SilkSheet open={open} onOpenChange={onOpenChange} variant="detached" size="sm" title={t1} description={t2} footer={t8} />;
    $[19] = onOpenChange;
    $[20] = open;
    $[21] = t1;
    $[22] = t2;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
interface DraftSavedPillProps {
  onClearDraft: () => void;
}

/** Inline footer pill — "✓ Draft saved · Clear" — matches the
 *  create-proposal flow's affordance. The Clear text-link opens a
 *  confirmation modal that resets the draft. */
function DraftSavedPill(t0) {
  const $ = _c(12);
  const {
    onClearDraft
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes.wizard");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={Tick02Icon} className="size-3 text-feedback-success-content" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("draftSaved");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span aria-hidden="true">·</span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("clearDraft");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onClearDraft || $[7] !== t4) {
    t5 = <button type="button" onClick={onClearDraft} className="rounded-item text-foreground-secondary underline-offset-2 transition-colors hover:text-foreground-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary">{t4}</button>;
    $[6] = onClearDraft;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t5) {
    t6 = <span className="flex items-center gap-1 text-caption text-foreground-tertiary">{t1}{t2}{t3}{t5}</span>;
    $[9] = t2;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}

/**
 * Resolve a picked outcome image to a real upload URL.
 *
 * The market image picker uses a DEFERRED lifecycle: `onImagePending` hands back
 * a `{ croppedFile, previewUrl }` draft whose `previewUrl` is a local
 * `blob:` (createObjectURL). The wizard used to store that blob directly, but
 * the submit gate rejects `blob:` URLs (`isImageResolved`) and `create()` needs
 * a real URL — so a manually picked image left "Submit suggestion" permanently
 * disabled even with the reason filled (MOR-824). Upload the cropped file to
 * IPFS and write the resolved URL into the store; show the blob meanwhile so the
 * preview is instant.
 */
function useOutcomeImageUpload() {
  const $ = _c(10);
  const store = useSuggestOutcomeWizardStore();
  const uploadMutation = useUploadMarketImages();
  const activeUploadIdRef = useRef(0);
  const activeBlobUrlRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (activeBlobUrlRef.current !== null) {
        URL.revokeObjectURL(activeBlobUrlRef.current);
        activeBlobUrlRef.current = null;
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const revokeActiveBlob = t0;
  let t1;
  if ($[1] !== store || $[2] !== uploadMutation) {
    t1 = draft => {
      const uploadId = activeUploadIdRef.current = activeUploadIdRef.current + 1;
      revokeActiveBlob();
      activeBlobUrlRef.current = draft.previewUrl;
      store.setImage(draft.previewUrl, store.imageQuery);
      uploadMutation.mutateAsync({
        files: [draft.croppedFile]
      }).then(result => {
        if (uploadId !== activeUploadIdRef.current) {
          return;
        }
        const url = result.images[0]?.url;
        const {
          setImage,
          removeImage,
          imageQuery
        } = useSuggestOutcomeWizardStore.getState();
        if (url === undefined) {
          removeImage();
        } else {
          setImage(url, imageQuery);
        }
        revokeActiveBlob();
      }).catch(err => {
        if (uploadId !== activeUploadIdRef.current) {
          return;
        }
        reportError(err, {
          feature: "suggest-outcome-image-upload"
        });
        useSuggestOutcomeWizardStore.getState().removeImage();
        revokeActiveBlob();
      });
    };
    $[1] = store;
    $[2] = uploadMutation;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const resolve = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      activeUploadIdRef.current = activeUploadIdRef.current + 1;
      revokeActiveBlob();
      useSuggestOutcomeWizardStore.getState().removeImage();
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const clear = t2;
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const blobRef = activeBlobUrlRef;
      return () => {
        if (blobRef.current !== null) {
          URL.revokeObjectURL(blobRef.current);
        }
      };
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== resolve || $[8] !== uploadMutation.isPending) {
    t5 = {
      resolve,
      clear,
      isUploading: uploadMutation.isPending
    };
    $[7] = resolve;
    $[8] = uploadMutation.isPending;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
interface StepManualOutcomeProps {
  marketTitle: string;
}
function StepManualOutcome(t0) {
  const $ = _c(20);
  const {
    marketTitle
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes.wizard");
  const store = useSuggestOutcomeWizardStore();
  const {
    resolve: resolveImage,
    clear: clearImage,
    isUploading
  } = useOutcomeImageUpload();
  let t1;
  if ($[0] !== isUploading || $[1] !== store.imageUrl || $[2] !== store.label || $[3] !== store.ticker || $[4] !== t) {
    t1 = !isUploading && store.label.trim().length > 0 && store.ticker.trim().length > 0 && !isResolvedImageUrl(store.imageUrl) ? t("outcomeImageRequiredError") : null;
    $[0] = isUploading;
    $[1] = store.imageUrl;
    $[2] = store.label;
    $[3] = store.ticker;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const imageError = t1;
  const t2 = store.imageUrl.length > 0 ? store.imageUrl : null;
  let t3;
  let t4;
  if ($[6] !== store) {
    t3 = label => store.setLabel(label);
    t4 = ticker => store.setTicker(ticker);
    $[6] = store;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== clearImage || $[10] !== imageError || $[11] !== isUploading || $[12] !== marketTitle || $[13] !== resolveImage || $[14] !== store.label || $[15] !== store.ticker || $[16] !== t2 || $[17] !== t3 || $[18] !== t4) {
    t5 = <div className="flex flex-col gap-3"><OutcomeInput index={0} label={store.label} ticker={store.ticker} marketTicker="" marketTitle={marketTitle} imageUrl={t2} onLabelChange={t3} onTickerChange={t4} onImagePending={resolveImage} onImageReject={clearImage} onImageRemove={clearImage} onRemove={_temp} canRemove={false} imageRequired={true} imageError={imageError} imageLoading={isUploading} /></div>;
    $[9] = clearImage;
    $[10] = imageError;
    $[11] = isUploading;
    $[12] = marketTitle;
    $[13] = resolveImage;
    $[14] = store.label;
    $[15] = store.ticker;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
function _temp() {}
function StepReasonConfirm(t0) {
  const $ = _c(27);
  const {
    creatorDirectAdd
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes.wizard");
  const store = useSuggestOutcomeWizardStore();
  let t1;
  if ($[0] !== creatorDirectAdd || $[1] !== t) {
    t1 = creatorDirectAdd ? t("confirmIntroCreator") : t("confirmIntro");
    $[0] = creatorDirectAdd;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <p className="text-caption text-foreground-secondary">{t1}</p>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== store.imageUrl || $[6] !== store.label || $[7] !== store.ticker) {
    t3 = <SuggestionPreviewCard label={store.label} ticker={store.ticker} imageUrl={store.imageUrl} />;
    $[5] = store.imageUrl;
    $[6] = store.label;
    $[7] = store.ticker;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t) {
    t4 = t("reasonLabel");
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = store.reason;
  let t6;
  if ($[11] !== store) {
    t6 = e => store.setReason(e.target.value);
    $[11] = store;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("reasonPlaceholder");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("reasonRequiredHint");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== store.reason || $[18] !== t4 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t9 = <TextareaField label={t4} value={t5} onChange={t6} maxLength={280} placeholder={t7} required={true} aria-required="true" description={t8} />;
    $[17] = store.reason;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t2 || $[24] !== t3 || $[25] !== t9) {
    t10 = <div className="flex flex-col gap-3">{t2}{t3}{t9}</div>;
    $[23] = t2;
    $[24] = t3;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
function StepDisclaimer(t0) {
  const $ = _c(26);
  const {
    ackChecked,
    onAckChange
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes.wizard");
  const store = useSuggestOutcomeWizardStore();
  let t1;
  if ($[0] !== store.imageUrl || $[1] !== store.label || $[2] !== store.ticker) {
    t1 = <SuggestionPreviewCard label={store.label} ticker={store.ticker} imageUrl={store.imageUrl} />;
    $[0] = store.imageUrl;
    $[1] = store.label;
    $[2] = store.ticker;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Icon icon={Alert01Icon} className="size-5 shrink-0 text-feedback-warning-content" aria-hidden="true" />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("disclaimerHeading");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <p className="text-heading-xs text-foreground-primary">{t3}</p>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("disclaimerBody");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <p className="text-caption text-foreground-secondary">{t5}</p>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = <div className="flex items-start gap-3 rounded-panel-row border border-feedback-warning-border/40 bg-feedback-warning-accent/10 p-4">{t2}<div className="flex flex-col gap-1">{t4}{t6}</div></div>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("disclaimerAcknowledge");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== ackChecked || $[19] !== onAckChange || $[20] !== t8) {
    t9 = <CheckboxField label={t8} checked={ackChecked} onCheckedChange={onAckChange} required={true} />;
    $[18] = ackChecked;
    $[19] = onAckChange;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t1 || $[23] !== t7 || $[24] !== t9) {
    t10 = <div className="flex flex-col gap-4">{t1}{t7}{t9}</div>;
    $[22] = t1;
    $[23] = t7;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}
function SuggestionPreviewCard(t0) {
  const $ = _c(13);
  const {
    label,
    ticker,
    imageUrl
  } = t0;
  let t1;
  if ($[0] !== imageUrl || $[1] !== label) {
    t1 = imageUrl.length > 0 ? <OptimizedImage variant="thumbnail" src={imageUrl} alt={label} size="size-12" sizes="48px" rounded="rounded-item" className="size-12 shrink-0" imageClassName="size-12 object-cover" /> : <div className="size-12 shrink-0 rounded-item bg-background-tertiary" />;
    $[0] = imageUrl;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== label) {
    t2 = <div className="truncate text-body font-medium text-foreground-primary">{label}</div>;
    $[3] = label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== ticker) {
    t3 = <div className="font-mono text-caption text-foreground-secondary">${ticker}</div>;
    $[5] = ticker;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="min-w-0">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t4) {
    t5 = <div className="flex items-center gap-3 rounded-panel-row border bg-background-secondary p-3">{t1}{t4}</div>;
    $[10] = t1;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
interface DeriveStepTitleArgs {
  step: WizardStep;
  creatorDirectAdd: boolean;
  t: ReturnType<typeof useTranslations<'launchpad.suggestedOutcomes.wizard'>>;
}
function deriveStepTitle({
  step,
  creatorDirectAdd,
  t
}: DeriveStepTitleArgs): string {
  if (step === 1) return t('stepManualOutcomeTitle');
  return creatorDirectAdd ? t('stepDisclaimerTitle') : t('stepReasonTitle');
}

/** A durable (uploaded) image URL — `blob:` previews are still uploading. */
function isResolvedImageUrl(url: string): boolean {
  return url.length > 0 && !url.startsWith('blob:');
}
function canAdvanceStep(store: ReturnType<typeof useSuggestOutcomeWizardStore.getState>, totalSteps: number, reasonRequired: boolean): boolean {
  if (store.step === 1) {
    // MOR-1818: the image is required on step 1. Submit is hard-gated on a
    // resolved image URL, so letting the user advance without one used to
    // leave "Submit suggestion" disabled with no visible reason.
    return store.label.trim().length > 0 && store.ticker.trim().length > 0 && isResolvedImageUrl(store.imageUrl);
  }
  if (store.step < totalSteps) {
    return store.label.trim().length > 0 && store.ticker.trim().length > 0;
  }
  // Final step requires a reason alongside the complete suggestion identity.
  const baseOk = store.label.trim().length > 0 && store.ticker.trim().length > 0;
  if (!reasonRequired) return baseOk;
  return baseOk && store.reason.trim().length > 0;
}

/**
 * Extract a duplicate-label conflict from the v2 SDK's `SdkError`. The SDK
 * parses the backend envelope into top-level fields:
 *   - `apiCode`     ← `error.code`
 *   - `details`     ← `error.details`
 * (See `packages/backend-sdk-v2/src/core/http-error.ts` for the full shape.)
 * Returns `null` for any non-duplicate error so the caller can fall back to a
 * generic toast.
 */
// ALLOW-UNKNOWN: SDK error envelope arrives untyped from the v2 mutation catch — narrow per field below.
function parseConflictError(err: unknown): ConflictState | null {
  if (typeof err !== 'object' || err === null) return null;
  // ALLOW-UNKNOWN: backend error envelope `apiCode` + `details` arrive opaque from the SDK catch — narrowed per field below.
  const sdkErr = err as {
    apiCode?: unknown;
    details?: unknown;
  };
  if (sdkErr.apiCode !== 'PROPOSAL_DUPLICATE_OUTCOME_LABEL') return null;
  const d = typeof sdkErr.details === 'object' && sdkErr.details !== null ?
  // ALLOW-UNKNOWN: dynamic conflict-details payload narrowed via per-field `typeof` guards below.
  sdkErr.details as Record<string, unknown> : {};
  const kind = typeof d.conflictKind === 'string' ? d.conflictKind : 'suggestion';
  const id = typeof d.conflictId === 'string' ? d.conflictId : '';
  const label = typeof d.conflictLabel === 'string' ? d.conflictLabel : '';
  const upvotes = typeof d.conflictUpvotes === 'number' ? d.conflictUpvotes : 0;
  const downvotes = typeof d.conflictDownvotes === 'number' ? d.conflictDownvotes : 0;
  return {
    kind: kind === 'onchain_outcome' ? 'onchain_outcome' : 'suggestion',
    id,
    label,
    upvotes,
    downvotes
  };
}